// FLAG:useDocuAi — pure visibility gate for the terminal failed-analysis retry action.
// The legacy action renderer never calls this helper when the flag is off; keeping every
// DocuAI-specific refusal here makes the menu and its tests one deletable feature slice.

interface FailedAnalysisRetryDocument {
  isAssigned?: boolean
  documentInvoiceDetails?: {
    reviewStatus?: string | null
  } | null
  metaData?: {
    analysisFailed?: boolean
    analysisTimedOut?: boolean
    analysisInProgress?: boolean
    ocr?: {
      isRunning?: boolean
      retryAllowed?: boolean
    } | null
  } | null
}

interface FailedAnalysisRetryActionContext {
  useDocuAiEnabled: boolean
  hasDocumentWriteRights: boolean
  isDesktop: boolean
  isIncomingInvoice: boolean
  isAllowedDocument: boolean
  document?: FailedAnalysisRetryDocument | null
}

interface FailedAnalysisRetryWriteAccessContext {
  folderReadOnly: boolean
  hasDocumentWriteRights: boolean
  canRetryFailedAnalysis: boolean
}

// A read-only Finance drawer must never turn broad module rights into a document mutation. Its
// only write signal is the server-computed capability for the exact terminal review row. Normal
// Documents drawers retain their established client Documents-WRITE gate.
export const resolveFailedAnalysisRetryWriteAccess = ({
  folderReadOnly,
  hasDocumentWriteRights,
  canRetryFailedAnalysis,
}: FailedAnalysisRetryWriteAccessContext): boolean => folderReadOnly
  ? canRetryFailedAnalysis
  : hasDocumentWriteRights

export const shouldShowFailedAnalysisRetryAction = ({
  useDocuAiEnabled,
  hasDocumentWriteRights,
  isDesktop,
  isIncomingInvoice,
  isAllowedDocument,
  document,
}: FailedAnalysisRetryActionContext): boolean => {
  const metaData = document?.metaData
  const ocr = metaData?.ocr
  const hasTerminalFailure = metaData?.analysisFailed === true
    || metaData?.analysisTimedOut === true
    || ocr?.retryAllowed === true

  return useDocuAiEnabled
    && hasDocumentWriteRights
    && !isDesktop
    && isIncomingInvoice
    && isAllowedDocument
    && !document?.isAssigned
    && !document?.documentInvoiceDetails?.reviewStatus
    && metaData?.analysisInProgress !== true
    && ocr?.isRunning !== true
    && hasTerminalFailure
    && ocr?.retryAllowed !== false
}
